import { Component, computed, DestroyRef, inject, Input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Owner, RealEstate, RealEstateService, Resident, Tenant, TenantUnitDetail, TenantUnitService } from '@eadsysteme/ewsrealestateclient-v3';
import { BasicShare, BasicShareService, BasicShareValue, BasicShareValueService } from '@eadsysteme/ewsrealestateclient-v2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../../../services/session.service';
import { forkJoin, Observable, ObservedValueOf } from 'rxjs';
import dayjs from 'dayjs';
import { MatDialog } from '@angular/material/dialog';
import { GanttOverlapService } from '../../../../services/gantt-overlap.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { DateUtil } from '../../../../util/DateUtil';
import { NumberUtil } from '../../../../util/NumberUtil';
import { BarTextStyle, GanttRow, GanttSegment, ParameterValueType } from '../../../util/ara-bar-chart/gantt.models';
import { ParameterValueGanttComponent } from '../../../billings/billing/tenantunit/billing-instances-master/billing-instance/gantt-chart/parameter-value-gantt.component';
import { DateRange, TenantUnitDateRangeFilterService } from '../../service/tenant-unit-date-range-filter.service';
import { BillingHelper } from '../../../../services/BillingHelper';
import { BillingService } from '@eadsysteme/billingclient-v1';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { DateRangeFilterDialogComponent } from '../../date-range-filter-dialog/date-range-filter-dialog.component';
import { Icons } from '../../../../util/Icons';

@Component({
	selector: 'ara-tenant-share-value',
	imports: [
		ParameterValueGanttComponent,
		TranslocoDirective,
		MatIcon,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		FormsModule,
		MatTooltipModule,
		MatIconButton,
	],
	templateUrl: './tenant-share-value.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './tenant-share-value.component.scss',
})
export class TenantShareValueComponent implements OnInit {
	private readonly basicShareValueService = inject(BasicShareValueService);
	private readonly basicShareService = inject(BasicShareService);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly translocoService = inject(TranslocoService);
	private readonly sessionService = inject(SessionService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	protected readonly dateUtil = inject(DateUtil);
	private readonly ganttOverlapService = inject(GanttOverlapService);
	private readonly numberUtil = inject(NumberUtil);
	protected readonly tenantUnitDateRangeFilter = inject(TenantUnitDateRangeFilterService);
	private readonly billingHelper = inject(BillingHelper);
	private readonly billingService = inject(BillingService);
	protected readonly icons = inject(Icons);

	@Input() currentTenantUnit!: TenantUnitDetail;
	@Input() currentRealEstate!: RealEstate;
	basicShares = signal<BasicShare[]>([]);
	basicShareValues = signal<BasicShareValue[]>([]);
	valuesByShare = signal<Map<BasicShare, BasicShareValue[]>>(new Map());
	finishedLoading = signal<boolean>(false);

	// Gantt chart layout constants (single source of truth for template)
	protected readonly GANTT_ROW_HEIGHT_MAIN = 50;
	protected readonly GANTT_ROW_HEIGHT = 40;
	protected readonly GANTT_BAR_HEIGHT = 26;
	protected readonly GANTT_AXIS_OFFSET = 12;

	protected myBarTextStyle: BarTextStyle = {
		color: 'black',
		fontSize: '12px',
		fontWeight: 'bold',
	};

	private readonly primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();

	protected readonly dayjs = dayjs;

	timelineData: GanttRow[] = [{ label: '', segments: [] }];
	// Raw data without date filtering - loaded once from server
	private readonly rawBasicShareData = signal<GanttRow[]>([]);
	private readonly rawRolesData = signal<GanttRow[]>([]);

	// Filtered data based on date range - auto-recompute when raw data or date range changes
	readonly basicShareData = computed(() => this.filterGanttRows(this.rawBasicShareData()));
	readonly rolesData = computed(() => this.filterGanttRows(this.rawRolesData()));

	// Flag to disable animation during timeline drag
	isDraggingTimeline = signal(false);
	earliestLockDate: string = null;

	protected startDate = computed(() => {
		const fromFilter = this.tenantUnitDateRangeFilter.validFrom$() ?? dayjs(DateUtil.minDate);
		// Use Day.js to snap to the beginning of the selected month.
		return fromFilter ? fromFilter.startOf('month').toDate() : new Date(new Date().getFullYear(), 0, 1);
	});

	protected endDate = computed(() => {
		const toFilter = this.tenantUnitDateRangeFilter.validTo$() ?? dayjs(DateUtil.maxDate);
		// Use Day.js to snap to the very end of the selected month.
		return toFilter ? toFilter.endOf('month').toDate() : new Date(new Date().getFullYear(), 11, 31);
	});

	private filterGanttRows(rows: GanttRow[]): GanttRow[] {
		const start = this.startDate();
		const end = this.endDate();

		return rows.map((row) => ({
			...row,
			segments: row.segments.filter((segment) => {
				if (start && segment.to < start) {
					return false;
				}
				return !(end && segment.from > end);
			}),
			overlaps: this.ganttOverlapService.calculateOverlapSegments(
				row.segments.filter((segment) => {
					if (start && segment.to < start) {
						return false;
					}
					return !(end && segment.from > end);
				})
			),
		}));
	}

	ngOnInit(): void {
		if (this.currentRealEstate && this.currentTenantUnit) {
			this.tenantUnitDateRangeFilter.realEstate = this.currentRealEstate;
			this.billingHelper.syncRealEstateBillings(this.currentRealEstate.area, this.currentRealEstate.id).subscribe(() => {
				this.loadGanttData(this.currentRealEstate, this.currentTenantUnit);
			});
		} else {
			const currentRealEstateId = this.activeRoute.snapshot.paramMap.get('realEstateId');
			const currentTenantUnitId = this.activeRoute.snapshot.paramMap.get('tenantUnitId');

			this.finishedLoading.set(false);
			if (currentRealEstateId && currentTenantUnitId) {
				forkJoin({
					realEstate: this.realEstateService.getRealEstate(this.sessionService.effectiveArea, currentRealEstateId),
					tenantUnit: this.tenantUnitService.getTenantUnitById(this.sessionService.effectiveArea, currentRealEstateId, currentTenantUnitId),
				})
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe(({ realEstate, tenantUnit }) => {
						this.currentRealEstate = realEstate;
						this.tenantUnitDateRangeFilter.realEstate = realEstate;
						this.earliestLockDate = this.dateUtil.formatDate(dayjs(realEstate.lockPortalAccessUntil), false);
						this.currentTenantUnit = tenantUnit;

						this.billingHelper.syncRealEstateBillings(this.currentRealEstate.area, this.currentRealEstate.id).subscribe(() => {
							this.loadGanttData(realEstate, tenantUnit);
						});
					});
			}
		}
	}

	private loadGanttData(realEstate: RealEstate, tenantUnit: TenantUnitDetail) {
		this.rawBasicShareData.set([]);
		this.rawRolesData.set([]);
		this.valuesByShare.set(new Map());

		this.finishedLoading.set(false);
		this.currentTenantUnit = tenantUnit;
		this.currentRealEstate = realEstate;

		this.rolesToGanttData(tenantUnit.owners, this.translocoService.translate('common.owner'));
		this.rolesToGanttData(tenantUnit.residents, this.translocoService.translate('common.resident'));
		this.rolesToGanttData(tenantUnit.tenants, this.translocoService.translate('common.tenant'));
		this.loadBasicShares(realEstate.id, tenantUnit.id);
	}

	private rolesToGanttData(roles: Array<Owner | Tenant | Resident>, roleLabel: string) {
		// Store raw segments without date filtering
		const roleSegments: GanttSegment[] = roles.map((currentRole: Owner | Tenant | Resident) => {
			return {
				valueId: currentRole.id,
				value: currentRole.person?.fullName ? currentRole.person.fullName : [currentRole.person?.givenName, currentRole.person?.familyName].join(' '),
				color: 'rgba(var(--color-primary), 0.08)',
				from: dayjs(currentRole.validFrom ?? DateUtil.minDate.toDate()).toDate(),
				to: dayjs(currentRole.validTo ?? DateUtil.maxDate.toDate()).toDate(),
			};
		});
		this.rawRolesData.update((rows) => [...rows, { label: roleLabel, segments: roleSegments, overlaps: [] }]);
	}

	private loadBasicShares(currentRealEstateId: string, currentTenantUnitId: string) {
		forkJoin({
			basicShares: this.basicShareService.listBasicShares(this.sessionService.effectiveArea, currentRealEstateId),
			basicShareValues: this.basicShareValueService.getBasicShareValues(this.sessionService.effectiveArea, currentRealEstateId, currentTenantUnitId),
		})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response) => {
				this.basicShareToGanttData(response);
			});
	}

	private basicShareToGanttData(response: {
		basicShares: ObservedValueOf<Observable<Array<BasicShare>>>;
		basicShareValues: ObservedValueOf<Observable<Array<BasicShareValue>>>;
	}) {
		response.basicShares = response.basicShares.sort((a, b) => {
			return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
		});
		this.basicShares.set(response.basicShares);
		this.basicShareValues.set(response.basicShareValues);

		for (const basicShare of this.basicShares()) {
			const shareValues = this.basicShareValues().filter((value) => {
				return value.basicShareId === basicShare.id;
			});
			this.valuesByShare().set(basicShare, shareValues);
		}

		for (const entry of this.valuesByShare().entries()) {
			const key = entry[0];
			const value = entry[1];
			// Store raw segments without date filtering
			const segments: GanttSegment[] = value.map((basicShareValue) => {
				// Hide unit if UNDEFINED or UNKNOWN
				const displayUnit = basicShareValue.unit && !['UNDEFINED', 'UNKNOWN'].includes(basicShareValue.unit) ? basicShareValue.unit : undefined;
				return {
					valueId: basicShareValue.id,
					value: this.numberUtil.scaleAndReplaceDot(basicShareValue.value, 2),
					unit: displayUnit,
					additionalData: basicShareValue.tenantShareType,
					additionalDataType: 'tenantShareType',
					color: 'rgba(var(--color-primary), 0.08)',
					from: dayjs(basicShareValue.validFrom ?? DateUtil.minDate.toDate()).toDate(),
					to: dayjs(basicShareValue.validTo ?? DateUtil.maxDate.toDate()).toDate(),
				};
			});

			this.rawBasicShareData.update((rows) => [...rows, { typeId: key.id, label: key.name, segments, overlaps: [] }]);
		}
		this.finishedLoading.set(true);
	}

	protected onDateRangeShift(event: { startDate: Date; endDate: Date }): void {
		// Snap to month boundaries for consistent display
		const newStart = dayjs(event.startDate).startOf('month');
		const newEnd = dayjs(event.endDate).endOf('month');

		// Update the date range filter service (this will update the datepickers above the tabs)
		this.tenantUnitDateRangeFilter.setDateRange(newStart, newEnd);
	}

	onDragStart(): void {
		this.isDraggingTimeline.set(true);
	}

	onDragEnd(): void {
		// Keep animation disabled for a short time after drag ends
		// to prevent animation from triggering on the final render
		setTimeout(() => {
			this.isDraggingTimeline.set(false);
		}, 100);
	}

	openDateRangeFilterDialog() {
		const dialogRef = this.dialog.open(DateRangeFilterDialogComponent, {
			width: '800px',
			data: {
				validFrom: this.tenantUnitDateRangeFilter.validFrom,
				validTo: this.tenantUnitDateRangeFilter.validTo,
				realEstate: this.currentRealEstate,
			},
			autoFocus: false,
			restoreFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: DateRange) => {
				if (result) {
					this.tenantUnitDateRangeFilter.setDateRange(result.validFrom, result.validTo);
				}
			});
	}

	protected readonly ParameterValueType = ParameterValueType;
}
